// src/config/arena.ts — İlk arena tanımı (DESIGN §8): 3×3 platform_6x6x2 = 18×18 u, üst yüzey y 0, R = 9 u.
// Altta 2 adet platform_6x6x4 dekor parçası (walkable false; yüzen görünüm). Sorgular (groundY/edgeDist) sim/arena.ts'de (WP2).
import type { ArenaDef, PlatformDef, Vec3 } from '../core/types.ts';
import { v3 } from '../core/vec3.ts';

/** Kit parçası ölçüleri katalogdan (assets/viewer/data/catalog.json): platform_6x6x2 size [6,2,6] min y 0 → max y 2; 6x6x4 [6,4,6]. */
export const KIT_PLATFORM_6x6x2 = 'blue/platform_6x6x2_blue';
export const KIT_PLATFORM_6x6x4 = 'blue/platform_6x6x4_blue';
export const KIT_TILE = 6;      // parça kenarı (u)
export const KIT_H2 = 2;        // 6x6x2 yüksekliği
export const KIT_H4 = 4;        // 6x6x4 yüksekliği

/** Tek kalibrasyon sabiti R (DESIGN §8.1): platform yarı genişliği; kb ölçeği = R/9, çift zıplama vy × √(R/9). */
export const ARENA_HALF_WIDTH = 9;
/** Üst yüzey y (parçalar y = topY − height = −2'ye yerleştirilir). */
export const ARENA_TOP_Y = 0;
/** Kill plane (DESIGN §8): y < −8 → Ev.Fell (0.62 s düşüş). */
export const ARENA_KILL_Y = -8;

function tile(ix: number, iz: number): PlatformDef {
  // ix, iz ∈ {−1, 0, 1}: merkezleri (±6, ±6); AABB [−9, 9]² toplam
  const cx = ix * KIT_TILE, cz = iz * KIT_TILE;
  return {
    minX: cx - KIT_TILE / 2, maxX: cx + KIT_TILE / 2, minZ: cz - KIT_TILE / 2, maxZ: cz + KIT_TILE / 2,
    topY: ARENA_TOP_Y, height: KIT_H2, kitModel: KIT_PLATFORM_6x6x2, walkable: true,
  };
}
/** Alt dekor parçası: yürünemez; kapsül testine girmez (walkable false, kapsülün altında). */
function under(minX: number, minZ: number, topY: number): PlatformDef {
  return { minX, maxX: minX + KIT_TILE, minZ, maxZ: minZ + KIT_TILE, topY, height: KIT_H4, kitModel: KIT_PLATFORM_6x6x4, walkable: false };
}

const platforms: PlatformDef[] = [];
for (let iz = -1; iz <= 1; iz++) for (let ix = -1; ix <= 1; ix++) platforms.push(tile(ix, iz));
// "merkezde kademeli" (DESIGN §8): iki 6x6x4 parça çapraz kaydırılmış, üst yüzeyleri −2 ve −6 → basamak görünümü (yalnız görsel;
// tam yerleşim belgede yok → WP0 kararı, arenaView'da ayarlanabilir).
platforms.push(under(-4.5, -1.5, ARENA_TOP_Y - KIT_H2));
platforms.push(under(-1.5, -4.5, ARENA_TOP_Y - KIT_H2 - KIT_H4));

/**
 * Spawn noktaları — SEKİZ tane: dört köşe (±4.5, ±4.5) + dört kenar ortası (0/±6). Yeniden doğuşta tüm rakiplere
 * en uzak olanı seçilir (DESIGN §8).
 * NEDEN sekiz: `match.ts` maç başında `i % spawns.length` kullanıyor; dört nokta varken 5-8 dövüşçüde 0&4, 1&5, 2&6,
 * 3&7 ÜST ÜSTE doğuyordu ve ayrılmayı kapsül itişmesine bırakıyordu (ağ maçında kadro 8'e çıkıyor — NETWORK.md §2.3).
 * SIRA KORUNDU: ilk dördü eskisiyle birebir aynı, yeniler sona eklendi → 1-4 kişilik maçlarda başlangıç yerleşimi
 * değişmedi. Değişen tek şey yeniden doğuş: `farthestSpawn` artık sekiz aday arasından seçiyor (daha iyi yerleşim).
 * Kenar ortaları ±6'da: eşya noktalarıyla aynı uzaklık (kenara 3 u), köşelerden 4.7 u ayrık.
 */
const spawns: Vec3[] = [
  v3(-4.5, 0, -4.5), v3(4.5, 0, -4.5), v3(-4.5, 0, 4.5), v3(4.5, 0, 4.5),
  v3(0, 0, -6), v3(6, 0, 0), v3(0, 0, 6), v3(-6, 0, 0),
];
/** Eşya doğma noktaları (±6, 0, ±6): kenara 3 u (risk teşviki, DESIGN §9); 3 u yükseklikten düşer (ITEMS.dropHeight). */
const itemSpawns: Vec3[] = [v3(-6, 0, -6), v3(6, 0, -6), v3(-6, 0, 6), v3(6, 0, 6)];

export const ARENA: ArenaDef = {
  platforms, spawns, itemSpawns, killY: ARENA_KILL_Y, center: v3(0, 0, 0), halfWidth: ARENA_HALF_WIDTH,
};
